import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useRouter } from 'react-resource-router';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import TableIcon from '@atlaskit/icon/core/table';
import { Anchor, Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { JiraAppSelectorIconWithGithub } from '../../../common/constants/icons/jira-app-selector-with-github';
import { DEFAULT_GAS_CHANNEL } from '../../../common/utils/events/constants';
import { onRovoNavigationClicked } from '../../../common/utils/events/rovo-navigation-interacted';
import { generateQueryMetrics } from '../../../utils/jira/analytics';
import { useRovoNavigationUrl } from '../../../utils/use-rovo-navigation-url';
import messages from './messages';

const ROVO_NAVIGATION_BUTTON_GNIN_TARGET = 'rovo-navigation-button-gnin';
const ROVO_NAVIGATION_BUTTON_FPS_TARGET = 'rovo-navigation-button-fps';

const styles = cssMap({
	ninWrapper: {
		marginLeft: token('space.050'),
		marginRight: token('space.050'),
	},
});

const ISSUE_NAVIGATOR = 'issue-navigator' as const;
const FULL_PAGE_SEARCH = 'full-page-search' as const;

export type RovoSearchIssueNavigatorButtonProps = {
	experience: typeof ISSUE_NAVIGATOR | typeof FULL_PAGE_SEARCH;
	sainJql?: string | null;
	// ADS appearance of the inner Button.
	buttonAppearance?: 'default' | 'subtle';
};

export const RovoButton: ({
	experience,
	sainJql,
	buttonAppearance,
}: RovoSearchIssueNavigatorButtonProps) => React.JSX.Element = ({
	experience,
	sainJql,
	buttonAppearance = 'default',
}: RovoSearchIssueNavigatorButtonProps) => {
	const [{ query }] = useRouter();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fullPageSearchUrl, issueNavigatorUrl, onRovoNavigate, text } = useRovoNavigationUrl({
		experience,
		queryString: query,
		sainJql,
	});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const ninQueryAnalytics = useMemo(() => generateQueryMetrics(query, text), []);
	// we only want to use the initial query value in NIN because queryHash needs to remain the same value across the whole experience while a user is on NIN
	const queryAnalytics = useMemo(
		() => (experience === FULL_PAGE_SEARCH ? generateQueryMetrics(query, text) : ninQueryAnalytics),
		[experience, query, ninQueryAnalytics, text],
	);

	const handleClick = useCallback(() => {
		onRovoNavigate();
		createAnalyticsEvent(
			onRovoNavigationClicked({
				source:
					experience === FULL_PAGE_SEARCH ? 'advancedSearchScreen' : 'globalIssueNavigatorScreen',
				experiment: 'button',
				...queryAnalytics,
			}),
		).fire(DEFAULT_GAS_CHANNEL);
	}, [createAnalyticsEvent, experience, queryAnalytics, onRovoNavigate]);

	const { nextViewUrl, label, iconBefore, iconAfter } = useMemo(() => {
		if (experience === ISSUE_NAVIGATOR) {
			return {
				nextViewUrl: fullPageSearchUrl,
				iconBefore: SearchIcon,
				iconAfter: JiraAppSelectorIconWithGithub,
				label: messages.searchApps,
			};
		}

		return {
			nextViewUrl: issueNavigatorUrl,
			iconBefore: TableIcon,
			iconAfter: undefined,
			label: messages.viewWorkItems,
		};
	}, [experience, fullPageSearchUrl, issueNavigatorUrl]);

	return (
		<Box
			testId={
				experience === FULL_PAGE_SEARCH
					? ROVO_NAVIGATION_BUTTON_FPS_TARGET
					: ROVO_NAVIGATION_BUTTON_GNIN_TARGET
			}
			xcss={experience === FULL_PAGE_SEARCH ? undefined : styles.ninWrapper}
		>
			<Anchor href={nextViewUrl} tabIndex={-1}>
				<Button
					appearance={buttonAppearance}
					iconBefore={iconBefore}
					onClick={handleClick}
					iconAfter={iconAfter}
				>
					{formatMessage(label)}
				</Button>
			</Anchor>
		</Box>
	);
};
